<template>
  <v-card>
    <v-toolbar>
      <v-toolbar-title>Courses</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-form @submit.stop.prevent="onSubmit">
        <v-text-field
          class="mr-6"
          v-model="search"
          :label="$i18n.translate('Filter') + ' ' + $i18n.translate('Courses')"
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          @click:clear="onClear"
        ></v-text-field>
      </v-form>
      <v-menu offset-y :close-on-content-click="false">
        <template v-slot:activator="{ on: onMenu }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: onTooltip }">
              <v-btn class="mr-2" icon v-on="{ ...onMenu, ...onTooltip }">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate("Advanced Search") }}</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item-group color="primary">
            <v-subheader>{{ $i18n.translate("Advanced Search") }}</v-subheader>
            <v-list-item selectable>
              <v-list-item-content>
                <v-select
                  dense
                  :items="effectiveItems"
                  v-model="filterByEffective"
                  :label="$i18n.translate('Active') + ' / ' + $i18n.translate('Inactive')"
                  clearable
                  @input="onSubmit"
                ></v-select>
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable>
              <v-list-item-content>
                <v-select
                  dense
                  :items="courseProductTypeList"
                  v-model="filterByCourseProductType"
                  :label="
                    $i18n.translate('Course') + '  ' + $i18n.translate('Product') + '  ' + $i18n.translate('Type')
                  "
                  clearable
                  @input="onSubmit"
                ></v-select>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
      <v-btn v-if="$privilege.hasPrivilege('COURSE_CREATE')" fab small :to="{ name: 'course', params: { id: 0 } }">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-data-table
        :items="items"
        :headers="headers"
        :server-items-length="total"
        :options.sync="options"
        :loading="loading"
      >
        <template v-slot:item.courseKey="{ item }">
          <router-link :to="{ name: 'course', params: { id: item.id } }">
            {{ item.courseKey }}
          </router-link>
        </template>

        <template v-slot:item.effectiveDate="{ item }">
          {{ item.effectiveDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
        </template>

        <template v-slot:item.expirationDate="{ item }">
          {{ item.expirationDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
        </template>

        <template v-slot:item.effective="{ item }">
          <v-chip color="primary" v-if="item.effective">{{ $i18n.translate("Active") }}</v-chip>
          <v-chip color="danger" v-else>{{ $i18n.translate("Inactive") }}</v-chip>
        </template>
        <template v-slot:item.updatedDate="{ item }">
          {{ item.updatedDate | formatDateFromNow }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Courses",
  metaInfo: {
    title: "Courses"
  },
  data() {
    return {
      loading: false,
      items: [],
      nameFilter: "",
      search: "",
      filterByEffective: "Active",
      effectiveItems: [
        { text: "Active", value: "Active" },
        { text: "Inactive", value: "Inactive" },
        { text: "All", value: "All" }
      ],
      courseProductTypeList: [],
      filterByCourseProductType: null,
      headers: [
        {
          value: "courseKey",
          text: "Key",
          sortable: true
        },
        {
          value: "name",
          text: "Name",
          sortable: true
        },
        {
          value: "passingGrade",
          text: "Passing Grade?",
          sortable: true,
          align: "right"
        },
        {
          value: "customFieldValue1",
          text: "Course Product Type",
          sortable: true
        },
        {
          value: "effectiveDate",
          text: this.$i18n.translate("Effective Date"),
          sortable: true
        },
        {
          value: "expirationDate",
          text: this.$i18n.translate("Expiration Date"),
          sortable: true
        },
        {
          value: "rankOrder",
          text: "Rank",
          sortable: true,
          align: "right"
        },
        {
          value: "effective",
          text: "Status",
          align: "center",
          sortable: false
        },
        {
          value: "updatedDate",
          text: "Last Updated",
          sortable: true
        }
      ],
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["updatedDate"],
        sortDesc: [true]
      }
    };
  },
  watch: {
    options: {
      handler() {
        this.getData();
      },
      deep: true
    }
  },
  methods: {
    onSubmit() {
      if (this.options.page !== 1) {
        this.options.page = 1;
      } else {
        this.getData();
      }
    },
    getData() {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      let filters = {};
      if (this.search && this.search.trim().length > 0) {
        filters.keyword = this.search.trim();
      }

      filters.isEffective = this.filterByEffective;
      filters.customFieldValue1 = this.filterByCourseProductType;

      this.$api
        .post(
          "/api/courses/search?size=" +
            itemsPerPage +
            "&page=" +
            (page - 1) +
            (sortBy && sortBy.length > 0
              ? "&sort=" + sortBy[0] + "," + ((sortDesc && sortDesc.length > 0) & sortDesc[0] ? "DESC" : "ASC")
              : ""),
          filters
        )
        .then(({ data }) => {
          this.loading = false;
          this.items = data.content;
          this.total = data.totalElements;
        })
        .catch(error => {
          this.loading = false;
          console.log(error);
        });
    },
    onClear() {
      this.search = "";
      this.onSubmit();
    },
    populateCourseProductTypeList() {
      if (
        this.selectedProgram &&
        this.selectedProgram.courseCustomFields &&
        this.selectedProgram.courseCustomFields.length > 0 &&
        this.selectedProgram.courseCustomFields[0].fieldValues
      ) {
        this.courseProductTypeList = this.selectedProgram.courseCustomFields[0].fieldValues
          .split("|")
          .map(e => e.trim());
      }
    }
  },
  mounted() {
    this.$store.dispatch("setBreadcrumb", [
      {
        text: this.$i18n.translate("Dashboard"),
        to: { name: "dashboard" },
        exact: true
      },
      {
        text: this.$i18n.translate("Clients"),
        to: { name: "clients" },
        exact: true
      },
      {
        text: this.selectedClient.name,
        to: {
          name: "client",
          params: { id: this.selectedClient.id },
          exact: true
        }
      },
      {
        text: this.$i18n.translate("Programs"),
        to: { name: "programs" },
        exact: true
      },
      {
        text: this.selectedProgram.name,
        to: {
          name: "program",
          params: { id: this.selectedProgram.id },
          exact: true
        }
      },
      { text: "Courses" }
    ]);

    this.populateCourseProductTypeList();
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram"])
  }
};
</script>
